import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IToken from '../../interfaces/IToken'
import { SearchRequest } from '../../services/api/interfaces/ApiRequest'
import { TokenGenerateRequest } from '../../services/api/interfaces/ApiRequest'
import { tokenList, tokenGenerate } from '../../services/api'

interface TokenContextState extends BaseContextState {
  tokens: Array<IToken> | null,
  token: IToken | null,
  email: string | null,
  password: string | null
}

interface TokenContextData extends TokenContextState {
  resetErrors: Function,
  resetTokenGenerated: Function,

  requestTokenList: (args: SearchRequest) => void,
  requestTokenFind: (args: SearchRequest) => void,
  requestTokenGenerate: (args: TokenGenerateRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as TokenContextData)

// Export consumer
export const TokenConsumer = Consumer

export class TokenProvider extends BaseContextProvider {
  state: TokenContextState = {
    tokens: null,
    token: null,
    email: null,
    password: null
  }

  /**
   * Reset token generated cache
   */
  resetTokenGenerated = () => {
    this.setState({
      email: null,
      password: null
    })
  }

  /**
   * Make api request for token list
   * 
   * @param {SearchRequest} payload 
   */
  requestTokenList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      const response = await tokenList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'tokens'
        },
      })
    })
  }

  /**
   * Make api request for token finder
   * 
   * @param {SearchRequest} payload 
   */
  requestTokenFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      token: null,
      email: null,
      password: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await tokenList(payload)

      // Process our response
      this.processResponse(response, {
        mappings: { destination: 'token' }
      })
    })
  }

  /**
   * Make api request for token creation
   * 
   * @param {TokenGenerateRequest} payload 
   */
  requestTokenGenerate = (payload: TokenGenerateRequest) => {
    this.setState({
      fetching: true,
      error: null,
      email: null,
      password: null
    }, async () => {
      const response = await tokenGenerate(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: [
          { origin: 'email', destination: 'email' },
          { origin: 'password', destination: 'password' }
        ]
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          resetTokenGenerated: this.resetTokenGenerated,
          requestTokenList: this.requestTokenList,
          requestTokenFind: this.requestTokenFind,
          requestTokenGenerate: this.requestTokenGenerate,
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}

